/**
 * Centralized device detection utility
 * Provides consistent device type detection across the application
 * with special handling for tablet portrait mode
 */

/**
 * Checks if the current device is a phone (not a tablet)
 * @returns {boolean} True if device is a phone
 */
export const isPhone = () => {
  if (typeof window === 'undefined') return false;

  const userAgent = navigator.userAgent.toLowerCase();
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  // Priority check: screens >= 768px wide are NOT phones (they're tablets or desktops)
  if (screenWidth >= 768) {
    return false;
  }

  // Check for phone-specific user agent strings (removed 'android' since it can be tablet)
  const phoneUserAgents = [
    'iphone', 'blackberry', 'nokia',
    'opera mini', 'windows mobile', 'windows phone', 'iemobile'
  ];

  const isPhoneUserAgent = phoneUserAgents.some(agent => userAgent.includes(agent));

  // Check if it's a small screen (phones are typically < 768px width)
  const isSmallScreen = screenWidth < 768;

  // Check if it's a touchscreen device
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // It's a phone if:
  // 1. Has phone user agent, OR
  // 2. Small screen AND touch device AND not an iPad/tablet
  const isTabletUserAgent = userAgent.includes('ipad') ||
                            (userAgent.includes('android') && !userAgent.includes('mobile'));

  return isPhoneUserAgent || (isSmallScreen && isTouchDevice && !isTabletUserAgent);
};

/**
 * Checks if the current device is a tablet in portrait orientation
 * @returns {boolean} True if device is a tablet in portrait mode
 */
export const isTabletPortrait = () => {
  if (typeof window === 'undefined') return false;

  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  // Tablet portrait is typically 768px-1024px width and taller than wide
  const isTabletWidth = screenWidth >= 768 && screenWidth <= 1024;
  const isPortraitOrientation = screenHeight > screenWidth;

  // Check for tablet user agents
  const userAgent = navigator.userAgent.toLowerCase();
  const isTabletUserAgent = userAgent.includes('ipad') ||
                            (userAgent.includes('android') && !userAgent.includes('mobile'));

  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  return isTabletWidth && isPortraitOrientation && isTouchDevice;
};

/**
 * Checks if the current device is a tablet in landscape orientation
 * @returns {boolean} True if device is a tablet in landscape mode
 */
export const isTabletLandscape = () => {
  if (typeof window === 'undefined') return false;

  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  // Tablet landscape is typically > 768px width and wider than tall
  const isTabletWidth = screenWidth >= 768 && screenWidth <= 1366;
  const isLandscapeOrientation = screenWidth > screenHeight;

  // Check for tablet user agents
  const userAgent = navigator.userAgent.toLowerCase();
  const isTabletUserAgent = userAgent.includes('ipad') ||
                            (userAgent.includes('android') && !userAgent.includes('mobile'));

  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  return isTabletWidth && isLandscapeOrientation && isTouchDevice;
};

/**
 * Checks if the current device is a tablet (any orientation)
 * @returns {boolean} True if device is a tablet
 */
export const isTablet = () => {
  return isTabletPortrait() || isTabletLandscape();
};

/**
 * Checks if the current device is a desktop/laptop
 * @returns {boolean} True if device is a desktop
 */
export const isDesktop = () => {
  if (typeof window === 'undefined') return true; // Default to desktop for SSR

  const screenWidth = window.innerWidth;
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Desktop is typically > 1024px OR not a touch device
  return screenWidth > 1024 || !isTouchDevice;
};

/**
 * Gets the current device type as a string
 * @returns {'phone' | 'tablet-portrait' | 'tablet-landscape' | 'desktop'} Device type
 */
export const getDeviceType = () => {
  if (isPhone()) return 'phone';
  if (isTabletPortrait()) return 'tablet-portrait';
  if (isTabletLandscape()) return 'tablet-landscape';
  return 'desktop';
};

/**
 * Hook for React components to detect device type with automatic updates on resize
 * @param {function} callback - Callback function to run when device type changes
 * @returns {function} Cleanup function to remove event listener
 */
export const setupDeviceDetection = (callback) => {
  if (typeof window === 'undefined') return () => {};

  const handleResize = () => {
    callback(getDeviceType());
  };

  // Call immediately
  callback(getDeviceType());

  // Listen for resize and orientation changes
  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', handleResize);

  // Return cleanup function
  return () => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('orientationchange', handleResize);
  };
};

/**
 * Helper to check if device should show mobile/compact UI
 * @returns {boolean} True if should show mobile UI
 */
export const shouldShowMobileUI = () => {
  return isPhone();
};

/**
 * Helper to check if device should show tablet-optimized UI
 * @returns {boolean} True if should show tablet UI
 */
export const shouldShowTabletUI = () => {
  return isTablet();
};

/**
 * Helper to check if device should show desktop UI
 * @returns {boolean} True if should show desktop UI
 */
export const shouldShowDesktopUI = () => {
  return isDesktop() && !isTablet();
};

export default {
  isPhone,
  isTabletPortrait,
  isTabletLandscape,
  isTablet,
  isDesktop,
  getDeviceType,
  setupDeviceDetection,
  shouldShowMobileUI,
  shouldShowTabletUI,
  shouldShowDesktopUI
};
